/**
 * TaskHistoryTab — Aba "Histórico" do modal de detalhes da tarefa
 *
 * Exibe timeline cronológica de eventos gravados em `task_activity_log`:
 * criação, alterações de fase/status/responsável/prioridade/prazo, edições
 * de título/descrição e exclusão.
 *
 * RELEASE: 2026-06-11 v1.0.0 — Criação inicial.
 */
import { useMemo } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  ArrowRightLeft,
  Activity,
  UserCheck,
  UserX,
  Flag,
  CalendarClock,
  FolderTree,
  Tags,
  History,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import {
  useTaskActivityLog,
  type TaskActivityAction,
  type TaskActivityLogEntry,
} from '@/hooks/useTaskActivityLog';

interface TaskHistoryTabProps {
  taskId: string;
}

/** Mapeamento ação → ícone + cor de fundo. */
const ACTION_META: Record<
  TaskActivityAction,
  { icon: typeof Plus; label: string; tone: string }
> = {
  created:              { icon: Plus,           label: 'Tarefa criada',              tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  updated:              { icon: Pencil,         label: 'Tarefa editada',             tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  deleted:              { icon: Trash2,         label: 'Tarefa excluída',            tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  phase_changed:        { icon: ArrowRightLeft, label: 'Fase alterada',              tone: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  status_changed:       { icon: Activity,       label: 'Status alterado',            tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  assigned:             { icon: UserCheck,      label: 'Responsável atribuído',      tone: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  unassigned:           { icon: UserX,          label: 'Responsável removido',       tone: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400' },
  priority_changed:     { icon: Flag,           label: 'Prioridade alterada',        tone: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  due_date_changed:     { icon: CalendarClock,  label: 'Prazo alterado',             tone: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  category_changed:     { icon: FolderTree,     label: 'Categoria alterada',         tone: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  classification_changed: { icon: Tags,         label: 'Classificação alterada',     tone: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
};

const STATUS_LABEL: Record<string, string> = {
  in_progress: 'Em Andamento',
  paused: 'Pausada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

const PRIORITY_LABEL: Record<string, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

/** Truncamento de UUID (padrão UI do projeto: 8 chars maiúsculas). */
function shortId(value: unknown): string {
  if (typeof value !== 'string') return '—';
  return value.length >= 8 ? value.slice(0, 8).toUpperCase() : value;
}

/** Remove tags HTML e colapsa espaços (para mostrar descrição em texto plano). */
function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Trunca string longa (descrição) preservando legibilidade. */
function truncate(value: string, max = 200): string {
  return value.length > max ? value.slice(0, max) + '…' : value;
}

/** Campos que armazenam UUID e devem ser resolvidos via `labels`. */
const UUID_FIELDS = new Set(['category_id', 'phase_id', 'classification_id', 'assigned_to']);

/** Renderiza valor "humanizado" para um campo conhecido. */
function renderValue(
  field: string,
  value: unknown,
  labels?: Record<string, string>
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'status') return STATUS_LABEL[String(value)] || String(value);
  if (field === 'priority') return PRIORITY_LABEL[String(value)] || String(value);
  if (field === 'due_date') {
    try {
      return format(new Date(String(value)), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return String(value);
    }
  }

  // Campos UUID: resolve nome via dicionário labels, mantém 8 chars como referência.
  if (UUID_FIELDS.has(field) && typeof value === 'string') {
    const name = labels?.[value];
    const sid = shortId(value);
    return name ? `${name} (${sid})` : sid;
  }

  if (typeof value === 'string') return value;
  return String(value);
}

const FIELD_LABEL: Record<string, string> = {
  title: 'Título',
  description: 'Descrição',
  phase_id: 'Fase',
  status: 'Status',
  assigned_to: 'Responsável',
  priority: 'Prioridade',
  due_date: 'Prazo',
  category_id: 'Categoria',
  classification_id: 'Classificação',
};

/** Renderiza o detalhamento das mudanças de uma linha do log. */
function ChangeLines({ entry }: { entry: TaskActivityLogEntry }) {
  const fields = Object.keys(entry.changes || {});
  if (fields.length === 0) return null;
  const labels = entry.labels;

  return (
    <ul className="mt-1.5 space-y-1 text-xs text-muted-foreground">
      {fields.map((field) => {
        const change = entry.changes[field] as { from?: unknown; to?: unknown; changed?: boolean };
        const label = FIELD_LABEL[field] || field;

        // Compatibilidade com logs antigos (descrição gravada como { changed: true })
        if (change?.changed && change.from === undefined && change.to === undefined) {
          return (
            <li key={field}>
              <span className="font-medium text-foreground">{label}</span>: alterado
            </li>
          );
        }

        // Descrição: mostrar texto plano truncado antes/depois
        if (field === 'description') {
          const fromTxt = truncate(stripHtml(String(change?.from ?? '')));
          const toTxt = truncate(stripHtml(String(change?.to ?? '')));
          return (
            <li key={field} className="break-words">
              <div>
                <span className="font-medium text-foreground">{label}</span>
              </div>
              <div className="mt-0.5 pl-2 border-l-2 border-border/50">
                <div className="line-through opacity-70">{fromTxt || '—'}</div>
                <div className="text-foreground">→ {toTxt || '—'}</div>
              </div>
            </li>
          );
        }

        return (
          <li key={field} className="break-words">
            <span className="font-medium text-foreground">{label}</span>:{' '}
            <span className="line-through opacity-70">{renderValue(field, change?.from, labels)}</span>
            {' → '}
            <span className="text-foreground">{renderValue(field, change?.to, labels)}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function TaskHistoryTab({ taskId }: TaskHistoryTabProps) {
  const { logs, isLoading, error } = useTaskActivityLog(taskId);

  // Mais recente no topo
  const ordered = useMemo(() => [...logs].reverse(), [logs]);

  if (isLoading) {
    return (
      <div className="space-y-3 p-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-8 w-8 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        Não foi possível carregar o histórico desta tarefa.
      </div>
    );
  }

  if (ordered.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-12 px-4">
        <History className="h-10 w-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm font-medium text-foreground">Sem histórico ainda</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-xs">
          As alterações desta tarefa aparecerão aqui automaticamente.
        </p>
      </div>
    );
  }

  return (
    <div className="relative pl-3 pr-1 py-2">
      {/* Linha vertical da timeline */}
      <div className="absolute left-[26px] top-3 bottom-3 w-px bg-border/60" />

      <ol className="space-y-3">
        {ordered.map((entry) => {
          const meta = ACTION_META[entry.action] || ACTION_META.updated;
          const Icon = meta.icon;
          const created = new Date(entry.created_at);

          return (
            <li key={entry.id} className="relative flex gap-3">
              {/* Ícone da ação */}
              <div
                className={cn(
                  'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-background',
                  meta.tone
                )}
              >
                <Icon className="h-4 w-4" />
              </div>

              {/* Bloco textual */}
              <div className="flex-1 min-w-0 pb-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={entry.performer_avatar || undefined} />
                    <AvatarFallback className="text-[10px]">
                      {(entry.performer_name || '?').slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium text-foreground truncate max-w-[200px]">
                    {entry.performer_name || 'Sistema'}
                  </span>
                  <span className="text-muted-foreground">{meta.label.toLowerCase()}</span>
                  <span
                    className="text-xs text-muted-foreground/70"
                    title={format(created, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                  >
                    · {formatDistanceToNow(created, { addSuffix: true, locale: ptBR })}
                  </span>
                </div>

                <ChangeLines entry={entry} />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
